import { useState, useEffect, useRef } from 'react';
import type { EmbedResult, EmbedCheckpoint, EmbedState } from '../../../../lib/embedProtocol';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import type { Theme } from '../slideTypes';
import type { EmbedStatus, EmbedSubmissionType } from '../../../../types';
import { springs } from '../../../ui/slide-animations';

// Re-export SlideSubmissionFeedback from the canonical types file
export type { SlideSubmissionFeedback } from '../../../../types';

// CSS for rich text content rendering (lists, bold, etc.)
export const RICH_TEXT_STYLES = `
  .slide-rich-content p, .slide-rich-editor p { margin-bottom: 0.75em; }
  .slide-rich-content p:last-child, .slide-rich-editor p:last-child { margin-bottom: 0; }
  .slide-rich-content ul, .slide-rich-editor ul { list-style-type: disc; padding-left: 1.5em; }
  .slide-rich-content ol, .slide-rich-editor ol { list-style-type: decimal; padding-left: 1.5em; }
  .slide-rich-content li, .slide-rich-editor li { margin-bottom: 0.2em; }
  .slide-rich-content strong, .slide-rich-editor strong { font-weight: 700; }
  .slide-rich-content em, .slide-rich-editor em { font-style: italic; }
  .slide-rich-editor { cursor: text; }
  .slide-rich-editor p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: currentColor;
    opacity: 0.35;
    pointer-events: none;
    height: 0;
  }
`;

// Feedback styling — single source for correct / warning / incorrect surfaces.
// Routes through theme.colors.feedback.* so success/miss respect the active theme
// (esp. dark + tenant-brand) instead of hardcoded #16a34a / #dc2626 / #f59e0b.
export type FeedbackKind = 'correct' | 'warning' | 'incorrect';

export function feedbackStyle(
  theme: Theme,
  kind: FeedbackKind,
): { bg: string; border: string; text: string } {
  const f = theme.colors.feedback;
  switch (kind) {
    case 'correct':
      return { bg: f.correctBg, border: f.correctBorder, text: f.correctText };
    case 'warning':
      return { bg: f.warningBg, border: f.warningBorder, text: f.warningText };
    case 'incorrect':
      return { bg: f.incorrectBg, border: f.incorrectBorder, text: f.incorrectText };
  }
}

// Embed event handler type for tracking interactions
export interface EmbedEventHandlers {
  onEmbedEvent?: (
    slideId: string,
    embedUrl: string,
    event: {
      status?: EmbedStatus;
      progress?: number;
      score?: number;
      /** Marks for this run-through, already validated (parseEmbedResult) and
       *  carrying an attemptId. Recorded per attempt in embed_results. */
      result?: EmbedResult;
      submissionData?: unknown;
      submissionType?: EmbedSubmissionType;
    }
  ) => void;
  onEmbedReady?: (slideId: string, embedUrl: string, sendInit: () => void) => void;
  /** The embed's own final button asked to move on — advance the slide as if the
   *  lesson chrome's Done had been pressed. Tracking is NOT this handler's job;
   *  'completed' already did that. */
  onEmbedAdvance?: (slideId: string) => void;
  /** A moment mid-run, already validated (parseEmbedCheckpoint) and carrying an
   *  attemptId. Recorded as one learning_interactions row per (attempt, step). */
  onEmbedCheckpoint?: (slideId: string, embedUrl: string, checkpoint: EmbedCheckpoint) => void;
  /** What the lesson already knows about this learner on this lesson, for the
   *  `state` field of lms-init. Resolves null when unknown. */
  getEmbedState?: (slideId: string) => Promise<EmbedState | null>;
}

// Responsive container - immersive (full-screen) when viewing, constrained when editing
export function SlideContainer({ children, theme, isEditing = true }: { children: React.ReactNode; theme: Theme; isEditing?: boolean }) {
  // Shared CSS custom property bridge — theme → CSS vars
  const slideVars = {
    '--slide-base-font': theme.typography.baseFont,
  } as React.CSSProperties;

  // Immersive mode (viewing) - fill entire container
  if (!isEditing) {
    return (
      <div
        className="w-full h-full slide-container"
        style={{
          containerType: 'size' as any,
          containerName: 'slide',
          backgroundColor: theme.colors.background,
          fontFamily: theme.fonts.body,
          ...slideVars,
        }}
      >
        {children}
      </div>
    );
  }

  // Editor mode — matches student view dimensions (no aspect-ratio card)
  return (
    <div
      className="w-full h-full slide-container"
      style={{
        containerType: 'size' as any,
        containerName: 'slide',
        backgroundColor: theme.colors.background,
        fontFamily: theme.fonts.body,
        ...slideVars,
      }}
    >
      {children}
    </div>
  );
}

// Settings gear button — appears on hover in the top-right of the slide
export function SettingsButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      // right-14 keeps clear of the canvas fullscreen button, which occupies
      // the same visual corner (it's stage chrome at right:14; we're slide
      // chrome, but the slide fills the stage so the corners coincide).
      className="absolute top-3 right-14 z-10 p-2 rounded-lg bg-black/20 hover:bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-all duration-150 backdrop-blur-sm"
      title="Slide settings"
      aria-label="Open slide settings"
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    </button>
  );
}

// Settings drawer — slides in from the right edge of the slide CANVAS (the
// editing stage), not the whole viewport: settings belong to the slide, so
// covering the topbar/outline reads as unnatural. Rendered via portal to
// document.body with fixed positioning measured off the stage rect — the
// slide itself zooms/pans (transformed ancestors re-anchor `fixed`), so
// anchoring inside it could open the drawer entirely off-screen.
export function SlideSettingsDrawer({
  isOpen,
  onClose,
  title,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  const [stageRect, setStageRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);

  // Close on Escape; measure the stage (re-measure on resize). Falls back to
  // the full viewport when no stage is mounted (e.g. fullscreen present mode).
  useEffect(() => {
    if (!isOpen) return;
    const measure = () => {
      const stage = document.querySelector('[data-slide-stage]');
      if (stage) {
        const r = stage.getBoundingClientRect();
        setStageRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else {
        setStageRect({ top: 0, left: 0, width: window.innerWidth, height: window.innerHeight });
      }
    };
    measure();
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('resize', measure);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !stageRect) return null;

  return createPortal(
    // kb-room (display:contents) re-establishes the builder design tokens —
    // the portal lands on document.body, OUTSIDE the .kb-room scope, so
    // var(--ink)/--line/--accent wouldn't resolve without it.
    <div className="kb-room" style={{ display: 'contents' }}>
      {/* Scrim — dims the canvas only, so the open state is visible without
          claiming the whole app */}
      <div
        className="fixed z-40 bg-black/25 slide-drawer-scrim"
        style={{ top: stageRect.top, left: stageRect.left, width: stageRect.width, height: stageRect.height }}
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Drawer */}
      <div
        className="fixed z-50 flex flex-col slide-drawer-panel"
        style={{
          top: stageRect.top,
          left: Math.max(stageRect.left, stageRect.left + stageRect.width - 320),
          height: stageRect.height,
          width: Math.min(320, stageRect.width),
          background: 'var(--surface-2)',
          borderLeft: '1px solid var(--line)',
          color: 'var(--ink)',
          fontFamily: 'var(--sans)',
          boxShadow: '-12px 0 32px -18px rgba(33,32,28,.35)',
        }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--line)' }}>
          <h3 style={{ fontFamily: 'var(--serif, var(--sans))', fontSize: 15, fontWeight: 500, letterSpacing: '-0.01em', margin: 0, color: 'var(--ink)' }}>
            {title}
          </h3>
          <button
            onClick={onClose}
            aria-label="Close settings"
            style={{ width: 28, height: 28, display: 'grid', placeItems: 'center', borderRadius: 6, border: '1px solid transparent', background: 'transparent', color: 'var(--ink-3)', cursor: 'pointer' }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--line)'; e.currentTarget.style.color = 'var(--ink)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.color = 'var(--ink-3)'; }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* Content */}
        <div className="flex-1 overflow-y-auto" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 18 }}>
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Drawer primitives — the shared visual vocabulary for ALL slide settings
// drawers (Text/Cover/Image/Video/Canva/Webapp). Templates compose these
// instead of hand-rolling Tailwind, so the drawers stay in the builder's
// register (kb-room tokens, coral accent) and in step with the template
// picker. Add controls here, not inline in templates.
// ─────────────────────────────────────────────────────────────────────────────

/** Section: mono caps label above its control(s) — mirrors the picker. */
export function DrawerSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontFamily: 'var(--mono, ui-monospace, monospace)', fontSize: 10.5, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 8 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

/** Selectable pill — layout/alignment options. Selected = coral accent. */
export function DrawerOption({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={selected}
      style={{
        padding: '6px 10px',
        fontSize: 12,
        fontFamily: 'inherit',
        fontWeight: selected ? 600 : 400,
        borderRadius: 6,
        cursor: 'pointer',
        border: `1px solid ${selected ? 'var(--accent)' : 'var(--line)'}`,
        background: selected ? 'var(--accent-soft)' : 'var(--surface)',
        color: selected ? 'var(--accent-ink, var(--accent))' : 'var(--ink-2)',
        transition: 'border-color 120ms, background 120ms',
      }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.borderColor = 'var(--line-2)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.borderColor = 'var(--line)'; }}
    >
      {children}
    </button>
  );
}

const drawerFieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  fontSize: 13,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  border: '1px solid var(--line)',
  borderRadius: 6,
  outline: 'none',
};

const focusField = (e: React.FocusEvent<HTMLElement>) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent)'; };
const blurField = (e: React.FocusEvent<HTMLElement>) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--line)'; };

export function DrawerInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} style={{ ...drawerFieldStyle, ...props.style }} onFocus={focusField} onBlur={blurField} />;
}

export function DrawerTextarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} style={{ ...drawerFieldStyle, resize: 'vertical', ...props.style }} onFocus={focusField} onBlur={blurField} />;
}

/** Checkbox row — accent-tinted check. */
export function DrawerCheckbox({
  id,
  checked,
  onChange,
  label,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: 'var(--accent)', width: 14, height: 14, cursor: 'pointer' }}
      />
      <label htmlFor={id} style={{ fontSize: 12.5, color: 'var(--ink-2)', cursor: 'pointer' }}>{label}</label>
    </div>
  );
}

/** Quiet destructive text-button (Remove background / Remove logo). */
export function DrawerRemove({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{ marginTop: 6, fontSize: 12, fontFamily: 'inherit', color: 'var(--live, #b91c1c)', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
      onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
      onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
    >
      {children}
    </button>
  );
}

/** Neutral help/info note — replaces the old per-drawer blue/purple/emerald
 *  boxes with one calm style; the title carries the differentiation. */
export function DrawerNote({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ padding: '10px 12px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 6, fontSize: 12, lineHeight: 1.55, color: 'var(--ink-2)' }}>
      <p style={{ fontWeight: 600, color: 'var(--ink)', margin: '0 0 4px', fontSize: 12 }}>{title}</p>
      {children}
    </div>
  );
}

/** Full-width action inside a drawer that copies text and confirms in place.
 *  The confirmation replaces the label rather than appearing beside it — there
 *  is no room in a drawer for a toast, and the button is where the eye already
 *  is. Reverts so a second copy still reads as an action. */
export function DrawerCopyButton({
  text,
  label,
  copiedLabel = 'Copied to clipboard',
}: {
  text: string;
  label: string;
  copiedLabel?: string;
}) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const fallbackRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (state !== 'copied') return;
    const t = setTimeout(() => setState('idle'), 2200);
    return () => clearTimeout(t);
  }, [state]);

  // Reveal-and-select rather than an error message. The clipboard can be denied
  // for reasons the author cannot act on (insecure context, browser policy), so
  // "it didn't work" is useless — hand them the text pre-selected and the copy
  // is one keystroke away. A button that silently does nothing on failure is the
  // exact disease this whole prompt exists to cure.
  useEffect(() => {
    if (state !== 'failed') return;
    fallbackRef.current?.focus();
    fallbackRef.current?.select();
  }, [state]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setState('copied');
    } catch {
      setState('failed');
    }
  };

  const lit = state === 'copied';

  return (
    <>
      <button
        onClick={copy}
        style={{
          width: '100%',
          padding: '8px 12px',
          fontSize: 12,
          fontFamily: 'inherit',
          fontWeight: 600,
          borderRadius: 6,
          cursor: 'pointer',
          border: `1px solid ${lit ? 'var(--accent)' : 'var(--line)'}`,
          background: lit ? 'var(--accent-soft)' : 'var(--surface)',
          color: lit ? 'var(--accent-ink, var(--accent))' : 'var(--ink)',
          transition: 'border-color 120ms, background 120ms, color 120ms',
        }}
        onMouseEnter={(e) => { if (!lit) e.currentTarget.style.borderColor = 'var(--line-2)'; }}
        onMouseLeave={(e) => { if (!lit) e.currentTarget.style.borderColor = 'var(--line)'; }}
      >
        {state === 'copied' ? copiedLabel
          : state === 'failed' ? "Couldn't reach the clipboard — copy from below"
          : label}
      </button>
      {state === 'failed' && (
        <textarea
          ref={fallbackRef}
          readOnly
          value={text}
          rows={6}
          style={{
            width: '100%',
            marginTop: 6,
            padding: 8,
            fontSize: 11,
            fontFamily: 'var(--mono, ui-monospace, monospace)',
            lineHeight: 1.5,
            color: 'var(--ink)',
            background: 'var(--ground, #EBEBEA)',
            border: '1px solid var(--line)',
            borderRadius: 4,
            resize: 'vertical',
          }}
        />
      )}
    </>
  );
}

/** Code sample inside a DrawerNote. */
export function DrawerCode({ children }: { children: React.ReactNode }) {
  return (
    <pre style={{ background: 'var(--ground, #EBEBEA)', border: '1px solid var(--line)', padding: 8, borderRadius: 4, fontSize: 11, fontFamily: 'var(--mono, ui-monospace, monospace)', color: 'var(--ink)', overflowX: 'auto', lineHeight: 1.6, margin: '6px 0 0' }}>
      {children}
    </pre>
  );
}

// Submission feedback banner — shows instructor feedback/score on graded slides
export function SubmissionFeedbackBanner({
  feedback,
  theme,
}: {
  feedback: import('../../../../types').SlideSubmissionFeedback;
  theme: Theme;
}) {
  const [expanded, setExpanded] = useState(true);
  if (!feedback.feedback && feedback.score === null) return null;

  return (
    <div
      className="absolute top-3 left-1/2 -translate-x-1/2 z-30 max-w-lg w-[90%] rounded-lg shadow-lg overflow-hidden cursor-pointer"
      style={{ backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.primary}40` }}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-center justify-between px-4 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: theme.colors.primary }}>
          Instructor Feedback
        </span>
        <div className="flex items-center gap-2">
          {feedback.score !== null && (
            <span
              className="text-sm font-bold px-2 py-0.5 rounded"
              style={{
                backgroundColor: feedbackStyle(
                  theme,
                  feedback.score >= 70 ? 'correct' : feedback.score >= 50 ? 'warning' : 'incorrect',
                ).text,
                color: 'white',
              }}
            >
              {feedback.score}%
            </span>
          )}
          <span className="text-xs" style={{ color: theme.colors.textMuted }}>
            {expanded ? '\u25B2' : '\u25BC'}
          </span>
        </div>
      </div>
      {expanded && feedback.feedback && (
        <div className="px-4 pb-3 text-sm" style={{ color: theme.colors.text }}>
          {feedback.feedback}
        </div>
      )}
    </div>
  );
}

// Shared submitted confirmation for activity slides
export function SurveySubmittedMessage({ theme }: { theme: Theme }) {
  return (
    <motion.div
      className="flex items-center gap-2 mt-4 px-4 py-2.5 rounded-lg"
      style={{ backgroundColor: theme.colors.primary + '15' }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springs.gentle as object}
    >
      <svg className="w-5 h-5" style={{ color: theme.colors.primary }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
      </svg>
      <span className="text-sm font-medium" style={{ color: theme.colors.primary }}>Response recorded</span>
    </motion.div>
  );
}
